import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import {
  createCart,
  addCartLines,
  updateCartLines,
  removeCartLines,
  getCart,
} from "@/lib/shopify";

const CartContext = createContext(null);
const CART_ID_KEY = "uniquely_pink_cart_id";

export function CartProvider({ children }) {
  const [cart, setCart] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Bootstrap cart from local storage
  useEffect(() => {
    const cached = localStorage.getItem(CART_ID_KEY);
    if (cached) {
      getCart(cached)
        .then((c) => {
          if (c && c.id) setCart(c);
          else localStorage.removeItem(CART_ID_KEY);
        })
        .catch(() => localStorage.removeItem(CART_ID_KEY));
    }
  }, []);

  const openCart = useCallback(() => setOpen(true), []);
  const closeCart = useCallback(() => setOpen(false), []);

  const addItem = useCallback(
    async (variantId, quantity = 1) => {
      setLoading(true);
      try {
        let updated;
        if (!cart) {
          updated = await createCart([{ merchandiseId: variantId, quantity }]);
          localStorage.setItem(CART_ID_KEY, updated.id);
        } else {
          updated = await addCartLines(cart.id, [
            { merchandiseId: variantId, quantity },
          ]);
        }
        setCart(updated);
        setOpen(true);
        return updated;
      } finally {
        setLoading(false);
      }
    },
    [cart],
  );

  const updateItem = useCallback(
    async (lineId, quantity) => {
      if (!cart) return;
      setLoading(true);
      try {
        const updated = await updateCartLines(cart.id, [
          { id: lineId, quantity },
        ]);
        setCart(updated);
      } finally {
        setLoading(false);
      }
    },
    [cart],
  );

  const removeItem = useCallback(
    async (lineId) => {
      if (!cart) return;
      setLoading(true);
      try {
        const updated = await removeCartLines(cart.id, [lineId]);
        setCart(updated);
      } finally {
        setLoading(false);
      }
    },
    [cart],
  );

  const totalQuantity = cart?.totalQuantity || 0;

  const value = {
    cart,
    open,
    openCart,
    closeCart,
    addItem,
    updateItem,
    removeItem,
    loading,
    totalQuantity,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
}
